import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut, ChevronDown, Facebook, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

export const Header = () => {
    const location = useLocation();
    const { cart } = useCart();
    const { user, isAuthenticated, logout } = useAuth();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const navLinks = [
        { href: '/', label: 'Home' },
        { href: '/shop', label: 'Shop' },
        { href: '/about', label: 'About' },
        { href: '/contact', label: 'Contact' },
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <header className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-md border-b border-slate-100">
            <div className="container-main">
                <div className="flex items-center justify-between h-20">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
                        <img 
                            src="/logo.png" 
                            alt="Henry's Aussie Fruit & Veggies" 
                            className="h-14 w-auto"
                        />
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-6">
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                to={link.href}
                                className={`text-sm font-medium transition-colors hover:text-[#1B4D3E] ${
                                    isActive(link.href) ? 'text-[#1B4D3E]' : 'text-slate-600'
                                }`}
                                data-testid={`nav-${link.label.toLowerCase()}`}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </nav>

                    {/* Right Side */}
                    <div className="flex items-center gap-3">
                        {/* Cart */}
                        <Link to="/cart" data-testid="cart-icon">
                            <Button variant="ghost" size="icon" className="relative">
                                <ShoppingCart className="w-5 h-5" />
                                {cart.item_count > 0 && (
                                    <Badge 
                                        className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center p-0 bg-orange-500 text-white text-xs cart-badge"
                                        data-testid="cart-badge"
                                    >
                                        {cart.item_count}
                                    </Badge>
                                )}
                            </Button>
                        </Link>

                        {/* Auth */}
                        {isAuthenticated ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="gap-2 hidden sm:flex" data-testid="user-menu-btn">
                                        <div className="w-8 h-8 rounded-full bg-[#1B4D3E] flex items-center justify-center">
                                            <span className="text-white text-sm font-medium">
                                                {user?.name?.charAt(0) || 'U'}
                                            </span>
                                        </div>
                                        <ChevronDown className="w-4 h-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                    <div className="px-2 py-1.5 text-sm font-medium text-slate-900">
                                        {user?.name || 'User'}
                                    </div>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem asChild>
                                        <Link to="/dashboard" className="cursor-pointer" data-testid="menu-dashboard">
                                            <User className="w-4 h-4 mr-2" />
                                            Dashboard
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                        onClick={logout}
                                        className="text-red-600 cursor-pointer"
                                        data-testid="menu-logout"
                                    >
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Logout
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <Link to="/login" className="hidden sm:block">
                                <Button variant="outline" size="sm" data-testid="header-login-btn">
                                    Login
                                </Button>
                            </Link>
                        )}

                        {/* Mobile Menu */}
                        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                            <SheetTrigger asChild className="md:hidden">
                                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                                    <Menu className="w-5 h-5" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="right" className="w-72">
                                <nav className="flex flex-col gap-4 mt-8">
                                    {navLinks.map((link) => (
                                        <Link
                                            key={link.href}
                                            to={link.href}
                                            onClick={() => setMobileMenuOpen(false)}
                                            className={`text-lg font-medium py-2 ${
                                                isActive(link.href) ? 'text-[#1B4D3E]' : 'text-slate-600'
                                            }`}
                                        >
                                            {link.label}
                                        </Link>
                                    ))}
                                    <hr className="my-2" />
                                    {isAuthenticated ? (
                                        <>
                                            <Link
                                                to="/dashboard"
                                                onClick={() => setMobileMenuOpen(false)}
                                                className="text-lg font-medium py-2 text-slate-600"
                                            >
                                                Dashboard
                                            </Link>
                                            <button
                                                onClick={() => { logout(); setMobileMenuOpen(false); }}
                                                className="text-lg font-medium py-2 text-red-600 text-left"
                                            >
                                                Logout
                                            </button>
                                        </>
                                    ) : (
                                        <Link
                                            to="/login"
                                            onClick={() => setMobileMenuOpen(false)}
                                            className="text-lg font-medium py-2 text-[#1B4D3E]"
                                        >
                                            Login / Register
                                        </Link>
                                    )}
                                </nav>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </div>
        </header>
    );
};

export const Footer = () => {
    return (
        <footer className="bg-[#1B4D3E] text-white py-12">
            <div className="container-main">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    {/* Brand */}
                    <div className="md:col-span-2">
                        <div className="flex items-center gap-3 mb-4">
                            <img 
                                src="/logo.png" 
                                alt="Henry's Aussie Fruit & Veggies" 
                                className="h-20 w-auto bg-white rounded-lg p-2"
                            />
                        </div>
                        <p className="text-white/70 max-w-md">
                            Fresh • Local • Quality. 
                            Quality wholesale produce for your business. 
                            Fresh Australian fruits and vegetables delivered to your door.
                        </p>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h3 className="font-semibold mb-4">Quick Links</h3>
                        <ul className="space-y-2 text-white/70">
                            <li><Link to="/shop" className="hover:text-white transition-colors">Shop</Link></li>
                            <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                            <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
                            <li><Link to="/login" className="hover:text-white transition-colors">Login</Link></li>
                        </ul>
                    </div>

                    {/* Contact */}
                    <div>
                        <h3 className="font-semibold mb-4">Contact</h3>
                        <ul className="space-y-2 text-white/70">
                            <li>Sydney, Australia</li>
                            <li>Pickup: Orange Grove Market, Lilyfield NSW 2040 <span className="text-white/50">(Sat 7am–1pm)</span></li>
                            <li>
                                <a href="mailto:henrys3@bigpond.com" className="hover:text-white transition-colors">
                                    henrys3@bigpond.com
                                </a>
                            </li>
                            <li>
                                <a href="tel:+61414829929" className="hover:text-white transition-colors">
                                    +61 414 829 929
                                </a>
                            </li>
                        </ul>
                        {/* Social Media */}
                        <div className="flex gap-3 mt-4">
                            <a 
                                href="https://www.facebook.com/p/Henrys-fresh-aussie-fruit-and-vegg-100057340634162/" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-10 h-10 bg-white/10 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors"
                                data-testid="footer-facebook"
                                aria-label="Facebook"
                            >
                                <Facebook className="w-5 h-5" />
                            </a>
                            <a 
                                href="https://www.instagram.com/henrysaussiefruitandveggies/" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-10 h-10 bg-white/10 hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500 rounded-full flex items-center justify-center transition-colors"
                                data-testid="footer-instagram"
                                aria-label="Instagram"
                            >
                                <Instagram className="w-5 h-5" />
                            </a>
                        </div>
                    </div>
                </div>

                <hr className="my-8 border-white/20" />

                <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/60">
                    <p>© {new Date().getFullYear()} Henry's Aussie Fruit & Veggies. All rights reserved.</p>
                    <div className="flex items-center gap-4">
                        <Link to="/privacy-policy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
                        <span className="text-white/30">|</span>
                        <Link to="/terms-of-service" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms of Service</Link>
                    </div>
                    <p>Delivery cut-off: Sat 12pm · Pickup cut-off: Thu 12pm (Sydney)</p>
                </div>

                <div className="mt-6 text-center text-xs text-white/40" data-testid="footer-credit">
                    Designed, developed &amp; managed by <span className="text-white/70 font-medium">Naviton Technologies Pty Ltd</span>
                </div>
            </div>
        </footer>
    );
};
